// SkinExpert/src/app/layout/account/AgentBuilder.tsx
import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Alert,
  Paper,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SettingsIcon from "@mui/icons-material/Settings";

interface AgentStep {
  id: string;
  type: "analysis" | "diagnosis" | "documentation" | "imaging" | "report" | "custom";
  name: string;
  description: string;
  config: Record<string, any>;
}

interface Agent {
  id: string;
  name: string;
  description: string;
  steps: AgentStep[];
  created: string;
  lastModified: string;
}

const STEP_TYPES = [
  {
    type: "analysis" as const,
    name: "Medical Analysis",
    icon: PsychologyIcon,
    description: "AI-powered medical image analysis",
    color: "#6366f1",
  },
  {
    type: "diagnosis" as const,
    name: "Diagnosis Assistant",
    icon: MedicalServicesIcon,
    description: "Generate diagnostic suggestions",
    color: "#ec4899",
  },
  {
    type: "documentation" as const,
    name: "Clinical Documentation",
    icon: DescriptionIcon,
    description: "Automated clinical note generation",
    color: "#8b5cf6",
  },
  {
    type: "imaging" as const,
    name: "Image Processing",
    icon: ImageSearchIcon,
    description: "Advanced image enhancement & segmentation",
    color: "#14b8a6",
  },
  {
    type: "report" as const,
    name: "Report Generation",
    icon: AssignmentIcon,
    description: "Comprehensive medical reports",
    color: "#f59e0b",
  },
  {
    type: "custom" as const,
    name: "Custom Workflow",
    icon: AccountTreeIcon,
    description: "Build your own workflow step",
    color: "#64748b",
  },
];

export default function AgentBuilder() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [agentDescription, setAgentDescription] = useState("");
  const [draggedStep, setDraggedStep] = useState<number | null>(null);

  const handleCreateAgent = () => {
    if (!agentName.trim()) return;

    const newAgent: Agent = {
      id: Date.now().toString(),
      name: agentName,
      description: agentDescription,
      steps: [],
      created: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    };

    setAgents([...agents, newAgent]);
    setCurrentAgent(newAgent);
    setAgentName("");
    setAgentDescription("");
    setDialogOpen(false);
  };

  const handleAddStep = (stepType: AgentStep["type"]) => {
    if (!currentAgent) return;

    const stepInfo = STEP_TYPES.find((s) => s.type === stepType);
    const newStep: AgentStep = {
      id: Date.now().toString(),
      type: stepType,
      name: stepInfo?.name || "Custom Step",
      description: stepInfo?.description || "",
      config: {},
    };

    const updatedAgent = {
      ...currentAgent,
      steps: [...currentAgent.steps, newStep],
      lastModified: new Date().toISOString(),
    };

    setCurrentAgent(updatedAgent);
    setAgents(agents.map((a) => (a.id === updatedAgent.id ? updatedAgent : a)));
    setStepDialogOpen(false);
  };

  const handleDeleteStep = (stepId: string) => {
    if (!currentAgent) return;

    const updatedAgent = {
      ...currentAgent,
      steps: currentAgent.steps.filter((s) => s.id !== stepId),
      lastModified: new Date().toISOString(),
    };

    setCurrentAgent(updatedAgent);
    setAgents(agents.map((a) => (a.id === updatedAgent.id ? updatedAgent : a)));
  };

  const handleDeleteAgent = (agentId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the agent when clicking delete
    
    const updatedAgents = agents.filter((a) => a.id !== agentId);
    setAgents(updatedAgents);
    
    // If we deleted the current agent, clear selection
    if (currentAgent?.id === agentId) {
      setCurrentAgent(null);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedStep(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedStep === null || draggedStep === index || !currentAgent) return;

    const steps = [...currentAgent.steps];
    const draggedItem = steps[draggedStep];
    steps.splice(draggedStep, 1);
    steps.splice(index, 0, draggedItem);

    const updatedAgent = {
      ...currentAgent,
      steps,
      lastModified: new Date().toISOString(),
    };

    setCurrentAgent(updatedAgent);
    setAgents(agents.map((a) => (a.id === updatedAgent.id ? updatedAgent : a)));
    setDraggedStep(index);
  };

  const handleDragEnd = () => {
    setDraggedStep(null);
  };

  const handleSaveAgent = () => {
    alert("Agent configuration saved! (Backend integration pending)");
  };

  const handleRunAgent = () => {
    alert("Running agent workflow... (Backend integration pending)");
  };

  return (
    <Box>
      <Box
        sx={{
          background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
          borderRadius: 3,
          p: 4,
          mb: 4,
          color: "white",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)",
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={2} position="relative">
          <SmartToyIcon sx={{ fontSize: 48 }} />
          <Box flex={1}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              AI Agent Builder
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Create custom medical workflow agents with drag-and-drop simplicity.
              Chain together analysis, diagnosis, and reporting steps.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.3)",
              },
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            New Agent
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Agents List */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <Box p={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Your Agents
              </Typography>
              {agents.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No agents yet. Create your first workflow agent!
                </Alert>
              ) : (
                <List sx={{ p: 0 }}>
                  {agents.map((agent) => (
                    <ListItem
                      key={agent.id}
                      button
                      onClick={() => setCurrentAgent(agent)}
                      selected={currentAgent?.id === agent.id}
                      secondaryAction={
                        <Tooltip title="Delete Agent" arrow>
                          <IconButton
                            edge="end"
                            onClick={(e) => handleDeleteAgent(agent.id, e)}
                            sx={{
                              color: "error.main",
                              "&:hover": { 
                                bgcolor: alpha("#ef4444", 0.1),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        "&.Mui-selected": {
                          bgcolor: alpha("#6366f1", 0.1),
                          "&:hover": {
                            bgcolor: alpha("#6366f1", 0.15),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <SmartToyIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={agent.name}
                        secondary={`${agent.steps.length} steps`}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Agent Builder */}
        <Grid item xs={12} md={8}>
          {currentAgent ? (
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box p={3}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={3}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {currentAgent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentAgent.description || "No description"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Save Agent">
                      <IconButton
                        onClick={handleSaveAgent}
                        sx={{
                          bgcolor: alpha("#6366f1", 0.1),
                          "&:hover": { bgcolor: alpha("#6366f1", 0.2) },
                        }}
                      >
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Run Agent">
                      <IconButton
                        onClick={handleRunAgent}
                        sx={{
                          bgcolor: alpha("#10b981", 0.1),
                          "&:hover": { bgcolor: alpha("#10b981", 0.2) },
                        }}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Workflow Steps
                </Typography>

                {currentAgent.steps.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                    No steps yet. Click "Add Step" to build your workflow.
                  </Alert>
                ) : (
                  <Box mb={2}>
                    {currentAgent.steps.map((step, index) => {
                      const stepInfo = STEP_TYPES.find((s) => s.type === step.type);
                      const StepIcon = stepInfo?.icon || AccountTreeIcon;

                      return (
                        <Paper
                          key={step.id}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: 2,
                            border: "2px solid",
                            borderColor: "divider",
                            cursor: "move",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: 2,
                            },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <DragIndicatorIcon color="action" />
                            <Chip
                              label={index + 1}
                              size="small"
                              sx={{
                                bgcolor: stepInfo?.color,
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                            <StepIcon sx={{ color: stepInfo?.color }} />
                            <Box flex={1}>
                              <Typography variant="body1" fontWeight={600}>
                                {step.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {step.description}
                              </Typography>
                            </Box>
                            <Tooltip title="Configure">
                              <IconButton size="small">
                                <SettingsIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteStep(step.id)}
                                sx={{
                                  color: "error.main",
                                  "&:hover": { bgcolor: alpha("#ef4444", 0.1) },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setStepDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    borderWidth: 2,
                    "&:hover": { borderWidth: 2 },
                  }}
                >
                  Add Step
                </Button>
              </Box>
            </Card>
          ) : (
            <Card
              sx={{
                borderRadius: 3,
                border: "2px dashed",
                borderColor: "divider",
                p: 6,
                textAlign: "center",
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select an agent or create a new one
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Build intelligent workflows by chaining medical AI steps
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create Agent Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
          Create New Agent
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Agent Name"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            margin="normal"
            placeholder="e.g., Skin Lesion Analysis Workflow"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            label="Description"
            value={agentDescription}
            onChange={(e) => setAgentDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder="Describe what this agent does..."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAgent}
            variant="contained"
            disabled={!agentName.trim()}
            sx={{ textTransform: "none", fontWeight: 600, px: 3 }}
          >
            Create Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Step Dialog */}
      <Dialog
        open={stepDialogOpen}
        onClose={() => setStepDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
          Add Workflow Step
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {STEP_TYPES.map((stepType) => {
              const StepIcon = stepType.icon;
              return (
                <Grid item xs={12} sm={6} key={stepType.type}>
                  <Paper
                    onClick={() => handleAddStep(stepType.type)}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: stepType.color,
                        boxShadow: `0 4px 20px ${alpha(stepType.color, 0.2)}`,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <StepIcon sx={{ fontSize: 32, color: stepType.color }} />
                      <Typography variant="h6" fontWeight={600}>
                        {stepType.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {stepType.description}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setStepDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
